import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Pin, PinOff } from 'lucide-react';
import { t } from '../../i18n/t';
import { usePages } from '../../hooks/usePages';
import type { Page } from '../../types/Page';
import { resolveIcon } from './sidebarIcons';
import { SidebarSearch } from './SidebarSearch';
import { SidebarRecent } from './SidebarRecent';

interface SidebarProps {
  isPinned: boolean;
  onTogglePin: () => void;
}

// Tree node materialized from the flat Page[] returned by GET /api/pages.
// A node is a "leaf" if it has a non-null `url` and no children; otherwise
// it's a group (clickable header that expands/collapses).
interface PageNode {
  pageId: number;
  pageCode: string;
  displayName: string;
  iconName: string | null;
  url: string | null;
  children: PageNode[];
}

function buildTree(pages: Page[]): PageNode[] {
  // Index children by parentPageId so each node's recurse is O(1) lookup.
  const childrenByParent = new Map<number, Page[]>();
  for (const p of pages) {
    if (p.parentPageId === null) continue;
    const arr = childrenByParent.get(p.parentPageId);
    if (arr) arr.push(p);
    else childrenByParent.set(p.parentPageId, [p]);
  }

  function recurse(p: Page): PageNode {
    const kids = (childrenByParent.get(p.pageId) ?? [])
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder);
    return {
      pageId: p.pageId,
      pageCode: p.pageCode,
      displayName: p.displayName,
      iconName: p.iconName,
      url: p.url,
      children: kids.map(recurse),
    };
  }

  // Top-level: parentPageId === null; hide `home` (logo handles navigation).
  const topLevel = pages
    .filter((p) => p.parentPageId === null && p.pageCode !== 'home')
    .sort((a, b) => a.sortOrder - b.sortOrder);
  return topLevel.map(recurse);
}

// Drop group nodes (no URL) whose recursive child set is empty. Leaves are
// always preserved. Server already permission-filters, but this still runs
// in case future additions introduce groups without descendants.
function pruneEmpty(nodes: PageNode[]): PageNode[] {
  return nodes.flatMap<PageNode>((node) => {
    const kids = pruneEmpty(node.children);
    if (node.url !== null) return [{ ...node, children: kids }];
    if (kids.length === 0) return [];
    return [{ ...node, children: kids }];
  });
}

// Expanded groups are DERIVED from the active screen, not persisted (Nitzan, 12/10/2026: the menu
// grew busy - keep it as closed as possible). Rules:
//   - whenever the active screen changes (tab switch, navigation, sidebar search) the menu collapses
//     to exactly the branch leading to that screen;
//   - opening a group by hand is an accordion: it closes every group that is not its ancestor.
// The old persisted key is dropped once (it would otherwise linger in the browser).
const LEGACY_EXPANDED_STORAGE_KEY = 'lop:sidebar:expanded';

// pageCode -> parent group's pageCode (null at the top level)
function buildParentMap(pages: Page[]): Map<string, string | null> {
  const byId = new Map(pages.map((p) => [p.pageId, p]));
  return new Map(
    pages.map((p) => [
      p.pageCode,
      p.parentPageId === null ? null : byId.get(p.parentPageId)?.pageCode ?? null,
    ]),
  );
}

// The groups that must be open for `pageCode` to be visible (its ancestors, not itself).
function ancestorsOf(pageCode: string, parents: Map<string, string | null>): Set<string> {
  const out = new Set<string>();
  let cur = parents.get(pageCode) ?? null;
  while (cur !== null && !out.has(cur)) {
    out.add(cur);
    cur = parents.get(cur) ?? null;
  }
  return out;
}

interface NodeRowProps {
  node: PageNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (pageCode: string) => void;
}

function NodeRow({ node, depth, expanded, onToggle }: NodeRowProps) {
  const paddingStart = 16 + depth * 16;
  const Icon = resolveIcon(node.iconName);

  if (node.url !== null) {
    return (
      <NavLink
        to={node.url}
        end={node.url === '/'}
        className={({ isActive }) =>
          isActive ? 'sidebar__item sidebar__item--active' : 'sidebar__item'
        }
        style={{ paddingInlineStart: paddingStart }}
        title={node.displayName}
      >
        {Icon && <Icon size={18} />}
        <span>{node.displayName}</span>
      </NavLink>
    );
  }

  const isOpen = expanded.has(node.pageCode);
  return (
    <div className="sidebar__group">
      <button
        type="button"
        className="sidebar__group-header sidebar__group-header--clickable"
        onClick={() => onToggle(node.pageCode)}
        aria-expanded={isOpen}
        style={{ paddingInlineStart: paddingStart }}
      >
        {Icon && <Icon size={16} />}
        <span className="sidebar__group-label">{node.displayName}</span>
        <ChevronDown
          size={14}
          className={
            'sidebar__chevron' + (isOpen ? ' sidebar__chevron--open' : '')
          }
          aria-hidden="true"
        />
      </button>
      {isOpen &&
        node.children.map((child) => (
          <NodeRow
            key={child.pageCode}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            onToggle={onToggle}
          />
        ))}
    </div>
  );
}

export function Sidebar({ isPinned, onTogglePin }: SidebarProps) {
  const { pages, loading, error } = usePages();
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_EXPANDED_STORAGE_KEY);
    } catch {
      // storage unavailable; ignore
    }
  }, []);

  const parents = useMemo(() => (pages ? buildParentMap(pages) : new Map<string, string | null>()), [pages]);

  // Active screen changed (the router location IS the active tab) -> show only its branch.
  // A route that isn't a menu page (home, user guide) closes everything.
  useEffect(() => {
    const page = pages?.find((p) => p.url === pathname);
    setExpanded(page ? ancestorsOf(page.pageCode, parents) : new Set());
  }, [pathname, pages, parents]);

  const tree = useMemo(
    () => (pages ? pruneEmpty(buildTree(pages)) : []),
    [pages],
  );

  const handleToggle = useCallback((pageCode: string) => {
    setExpanded((prev) => {
      if (prev.has(pageCode)) {
        // closing: drop the group and anything open beneath it
        const next = new Set<string>();
        prev.forEach((code) => {
          if (code !== pageCode && !ancestorsOf(code, parents).has(pageCode)) next.add(code);
        });
        return next;
      }
      // opening: accordion - keep only this group's own branch
      const next = ancestorsOf(pageCode, parents);
      next.add(pageCode);
      return next;
    });
  }, [parents]);

  const handleCollapsedGroupClick = useCallback(
    (pageCode: string) => {
      // In collapsed mode children are hidden — pin and pre-expand so the
      // user lands inside the group they tapped.
      setExpanded(() => {
        const next = ancestorsOf(pageCode, parents);
        next.add(pageCode);
        return next;
      });
      if (!isPinned) onTogglePin();
    },
    [isPinned, onTogglePin, parents],
  );

  return (
    <aside className="sidebar">
      {/* Top bar: search (pinned only) + recent-screens history + pin toggle,
          all on one row. Collapsed mode stacks the two icons vertically
          (column-reverse keeps the pin on top). */}
      <div
        className={
          'sidebar__header' +
          (isPinned ? '' : ' sidebar__header--collapsed')
        }
      >
        {isPinned && pages && <SidebarSearch pages={pages} />}
        <SidebarRecent pages={pages} collapsed={!isPinned} />
        <button
          type="button"
          className={isPinned ? 'sidebar__header-btn' : 'sidebar__collapsed-icon'}
          onClick={onTogglePin}
          title={isPinned ? t('common.unpin') : t('common.pin')}
          aria-label={isPinned ? t('common.unpin') : t('common.pin')}
        >
          {isPinned ? <Pin size={18} /> : <PinOff size={20} />}
        </button>
      </div>

      {isPinned ? (
        <>
          <nav className="sidebar__nav">
            {loading && (
              <div className="sidebar__status sidebar__status--loading">
                {t('common.loading')}
              </div>
            )}
            {error && (
              <div className="sidebar__status sidebar__status--error">
                {t('sidebar.loadError')}
              </div>
            )}
            {tree.map((node) => (
              <NodeRow
                key={node.pageCode}
                node={node}
                depth={0}
                expanded={expanded}
                onToggle={handleToggle}
              />
            ))}
          </nav>
        </>
      ) : (
        <nav className="sidebar__nav sidebar__nav--collapsed">
          {tree.map((node) => {
            const NodeIcon = resolveIcon(node.iconName);
            if (!NodeIcon) return null;
            if (node.url !== null) {
              return (
                <NavLink
                  key={node.pageCode}
                  to={node.url}
                  end={node.url === '/'}
                  className={({ isActive }) =>
                    isActive
                      ? 'sidebar__collapsed-icon sidebar__collapsed-icon--active'
                      : 'sidebar__collapsed-icon'
                  }
                  title={node.displayName}
                  aria-label={node.displayName}
                >
                  <NodeIcon size={20} />
                </NavLink>
              );
            }
            return (
              <button
                key={node.pageCode}
                type="button"
                className="sidebar__collapsed-icon"
                onClick={() => handleCollapsedGroupClick(node.pageCode)}
                title={node.displayName}
                aria-label={node.displayName}
              >
                <NodeIcon size={20} />
              </button>
            );
          })}
        </nav>
      )}
    </aside>
  );
}
